import React, { useEffect, useState } from 'react'
import { Modal, Button, Row, Col } from 'react-bootstrap'
import { BotUser } from '../../../../API/Users/models'
import { BsEyeFill, BsEyeSlashFill } from 'react-icons/bs'
import { toast } from 'react-hot-toast'
import { updateBotUser, toggleBlockedUser, deleteBotUser } from '../../../../API/Users/Users'
import { FaLock, FaLockOpen, FaTrash, FaCalendarAlt } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { getUserStatus } from '../utils/getUserStatus'

interface Props {
    show: boolean
    onHide: () => void
    user: BotUser | null
}

export const EditUserModal = ({ show, onHide, user }: Props) => {
    const [showPassword, setShowPassword] = useState(false)
    const [formData, setFormData] = useState({
        name: user?.name ?? '',
        password: user?.password ?? ''
    })
    const navigate = useNavigate()

    // Función para guardar cambios
    const handleSave = async () => {
        if (formData.name === '') {
            toast.error('El nombre es requerido')
            return
        }
        if (formData.password.length < 6) {
            toast.error('La contraseña debe tener al menos 6 caracteres')
            return
        }
        try {
            let result = await updateBotUser({
                userName: formData.name,
                newPassword: formData.password,
                userId: user?.id ?? ''
            })
            if (result?.id) {
                toast.success(`Usuario ${user?.name} actualizado correctamente`)
                onHide()
            } else {
                const errorMessage = result?.response?.data?.msg ?? 'Error al actualizar el usuario'
                toast.error(errorMessage)
            }
        } catch (error) {
            toast.error('Error al actualizar el usuario')
        }
    }

    // Función para bloquear/desbloquear usuario
    const handleToggleBlock = async () => {
        if (!user) return
        const response = await toggleBlockedUser({ userId: user?.id, blocked: !user?.blocked })
        if (response.success) {
            toast.success(`Usuario ${user?.blocked ? 'desbloqueado' : 'bloqueado'} correctamente`)
            onHide()
        } else {
            toast.error(`Error al ${user?.blocked ? 'desbloquear' : 'bloquear'} el usuario`)
        }
    }

    // Función para eliminar usuario
    const handleDelete = async () => {
        if (!user) return
        // if (!confirm(`¿Estás seguro de eliminar al usuario ${user.name}? Esta acción no se puede deshacer.`)) {
        //     return
        // }

        let result = await deleteBotUser({ userId: user?.id ?? '' })
        if (result?.msg) {
            toast.success(`Usuario ${user?.name} eliminado correctamente`)
            onHide()
        } else {
            const errorMessage = result?.response?.data?.msg ?? 'Error al eliminar el usuario'
            toast.error(errorMessage)
        }
    }

    // Función para ver calendario del usuario
    const handleViewCalendar = () => {
        navigate(`/admin/dashboard/user-sessions?user=${user?.id}`)
        onHide()
    }

    useEffect(() => {
        if (user) {
            setFormData({
                name: user.name,
                password: user.password
            })
        }
    }, [user])

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Gestionar Usuario: <span className="text-primary">{user?.name}</span></Modal.Title>
            </Modal.Header>

            <Modal.Body className='d-flex flex-column p-4'>
                <Row>
                    <Col md={6}>
                        <h5 className="mb-3">Información del usuario</h5>
                        <div className='mb-3'>
                            <label className='form-label'>Nombre</label>
                            <input
                                type="text"
                                className='form-control'
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>
                        <div className='mb-4'>
                            <label className='form-label'>Contraseña</label>
                            <div className='position-relative'>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    className='form-control'
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                />
                                <div
                                    onClick={() => setShowPassword(!showPassword)}
                                    className='cursor-pointer position-absolute'
                                    role='button'
                                    style={{ right: '10px', top: '40%', transform: 'translateY(-50%)' }}
                                >
                                    {showPassword ? <BsEyeSlashFill size={20} /> : <BsEyeFill size={20} />}
                                </div>
                            </div>
                        </div>
                        <Button variant="primary" onClick={handleSave} className="w-100">
                            Guardar cambios
                        </Button>
                    </Col>

                    <Col md={6}>
                        <h5 className="mb-3">Acciones adicionales</h5>
                        <div className="d-flex flex-column gap-3">
                            <Button
                                variant="outline-primary"
                                onClick={handleViewCalendar}
                                className="d-flex align-items-center justify-content-center gap-2"
                            >
                                <FaCalendarAlt /> Ver sesiones
                            </Button>

                            <Button
                                variant={user?.blocked ? "outline-success" : "outline-warning"}
                                onClick={handleToggleBlock}
                                className="d-flex align-items-center justify-content-center gap-2"
                            >
                                {user?.blocked ? <FaLockOpen /> : <FaLock />}
                                {user?.blocked ? "Desbloquear usuario" : "Bloquear usuario"}
                            </Button>

                            <Button
                                variant="outline-danger"
                                onClick={handleDelete}
                                className="d-flex align-items-center justify-content-center gap-2"
                            >
                                <FaTrash /> Eliminar usuario
                            </Button>
                        </div>

                        <div className="mt-4 p-3 rounded border text-white bg-dark">
                            <h6 className="mb-2">Estado actual:</h6>
                            <p className="mb-1">
                                <strong>Estado:{' '} </strong>
                                {user &&
                                    <span className={getUserStatus(user)?.color}>
                                        {getUserStatus(user)?.text}
                                    </span>
                                }
                            </p>
                            <p className="mb-1">
                                <strong>Último ingreso:</strong> {user?.loginDate ? new Date(user?.loginDate).toLocaleString("es-AR") : 'Nunca'}
                            </p>
                            <p className="mb-0">
                                <strong>Última acción:</strong> {user?.lastActionDate ? new Date(user?.lastActionDate).toLocaleString("es-AR") : 'Nunca'}
                            </p>
                        </div>
                    </Col>
                </Row>
            </Modal.Body>
        </Modal>
    )
}
